import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import styled, { useTheme } from 'styled-components';
import deepmerge from 'deepmerge';
import React, { useState, useRef, useEffect } from 'react';

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __rest(s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
}

const cssColorProperties = [
    'background',
    'backgroundColor',
    'borderColor',
    'borderTopColor',
    'borderBottomColor',
    'borderLeftColor',
    'borderRightColor',
    'color',
    'outlineColor',
    'fill',
    'stroke',
];

const standardLonghandProperties = [
    'alignContent',
    'alignItems',
    'alignSelf',
    'animationDelay',
    'animationDirection',
    'animationDuration',
    'animationFillMode',
    'animationIterationCount',
    'animationName',
    'animationPlayState',
    'animationTimingFunction',
    'appearance',
    'aspectRatio',
    'backdropFilter',
    'backfaceVisibility',
    'backgroundAttachment',
    'backgroundBlendMode',
    'backgroundClip',
    'backgroundColor',
    'backgroundImage',
    'backgroundOrigin',
    'backgroundPositionX',
    'backgroundPositionY',
    'backgroundRepeat',
    'backgroundSize',
    'blockOverflow',
    'blockSize',
    'borderBlockColor',
    'borderBlockEndColor',
    'borderBlockEndStyle',
    'borderBlockEndWidth',
    'borderBlockStartColor',
    'borderBlockStartStyle',
    'borderBlockStartWidth',
    'borderBlockStyle',
    'borderBlockWidth',
    'borderBottomColor',
    'borderBottomLeftRadius',
    'borderBottomRightRadius',
    'borderBottomStyle',
    'borderBottomWidth',
    'borderCollapse',
    'borderEndEndRadius',
    'borderEndStartRadius',
    'borderImageOutset',
    'borderImageRepeat',
    'borderImageSlice',
    'borderImageSource',
    'borderImageWidth',
    'borderInlineColor',
    'borderInlineEndColor',
    'borderInlineEndStyle',
    'borderInlineEndWidth',
    'borderInlineStartColor',
    'borderInlineStartStyle',
    'borderInlineStartWidth',
    'borderInlineStyle',
    'borderInlineWidth',
    'borderLeftColor',
    'borderLeftStyle',
    'borderLeftWidth',
    'borderRightColor',
    'borderRightStyle',
    'borderRightWidth',
    'borderSpacing',
    'borderStartEndRadius',
    'borderStartStartRadius',
    'borderTopColor',
    'borderTopLeftRadius',
    'borderTopRightRadius',
    'borderTopStyle',
    'borderTopWidth',
    'bottom',
    'boxDecorationBreak',
    'boxShadow',
    'boxSizing',
    'breakAfter',
    'breakBefore',
    'breakInside',
    'captionSide',
    'caretColor',
    'clear',
    'clipPath',
    'color',
    'colorAdjust',
    'columnCount',
    'columnFill',
    'columnGap',
    'columnRuleColor',
    'columnRuleStyle',
    'columnRuleWidth',
    'columnSpan',
    'columnWidth',
    'contain',
    'content',
    'counterIncrement',
    'counterReset',
    'counterSet',
    'cursor',
    'direction',
    'display',
    'emptyCells',
    'filter',
    'flexBasis',
    'flexDirection',
    'flexGrow',
    'flexShrink',
    'flexWrap',
    'float',
    'fontFamily',
    'fontFeatureSettings',
    'fontKerning',
    'fontLanguageOverride',
    'fontOpticalSizing',
    'fontSize',
    'fontSizeAdjust',
    'fontStretch',
    'fontStyle',
    'fontSynthesis',
    'fontVariant',
    'fontVariantCaps',
    'fontVariantEastAsian',
    'fontVariantLigatures',
    'fontVariantNumeric',
    'fontVariantPosition',
    'fontVariationSettings',
    'fontWeight',
    'gridAutoColumns',
    'gridAutoFlow',
    'gridAutoRows',
    'gridColumnEnd',
    'gridColumnStart',
    'gridRowEnd',
    'gridRowStart',
    'gridTemplateAreas',
    'gridTemplateColumns',
    'gridTemplateRows',
    'hangingPunctuation',
    'height',
    'hyphens',
    'imageOrientation',
    'imageRendering',
    'imageResolution',
    'initialLetter',
    'inlineSize',
    'inset',
    'insetBlock',
    'insetBlockEnd',
    'insetBlockStart',
    'insetInline',
    'insetInlineEnd',
    'insetInlineStart',
    'isolation',
    'justifyContent',
    'justifyItems',
    'justifySelf',
    'left',
    'letterSpacing',
    'lineBreak',
    'lineHeight',
    'lineHeightStep',
    'listStyleImage',
    'listStylePosition',
    'listStyleType',
    'marginBlock',
    'marginBlockEnd',
    'marginBlockStart',
    'marginBottom',
    'marginInline',
    'marginInlineEnd',
    'marginInlineStart',
    'marginLeft',
    'marginRight',
    'marginTop',
    'maskBorderMode',
    'maskBorderOutset',
    'maskBorderRepeat',
    'maskBorderSlice',
    'maskBorderSource',
    'maskBorderWidth',
    'maskClip',
    'maskComposite',
    'maskImage',
    'maskMode',
    'maskOrigin',
    'maskPosition',
    'maskRepeat',
    'maskSize',
    'maskType',
    'maxBlockSize',
    'maxHeight',
    'maxInlineSize',
    'maxLines',
    'maxWidth',
    'minBlockSize',
    'minHeight',
    'minInlineSize',
    'minWidth',
    'mixBlendMode',
    'motionDistance',
    'motionPath',
    'motionRotation',
    'objectFit',
    'objectPosition',
    'offsetAnchor',
    'offsetDistance',
    'offsetPath',
    'offsetRotate',
    'offsetRotation',
    'opacity',
    'order',
    'orphans',
    'outlineColor',
    'outlineOffset',
    'outlineStyle',
    'outlineWidth',
    'overflowAnchor',
    'overflowBlock',
    'overflowClipBox',
    'overflowInline',
    'overflowWrap',
    'overflowX',
    'overflowY',
    'overscrollBehaviorX',
    'overscrollBehaviorY',
    'paddingBlock',
    'paddingBlockEnd',
    'paddingBlockStart',
    'paddingBottom',
    'paddingInline',
    'paddingInlineEnd',
    'paddingInlineStart',
    'paddingLeft',
    'paddingRight',
    'paddingTop',
    'pageBreakAfter',
    'pageBreakBefore',
    'pageBreakInside',
    'paintOrder',
    'perspective',
    'perspectiveOrigin',
    'placeContent',
    'pointerEvents',
    'position',
    'quotes',
    'resize',
    'right',
    'rotate',
    'rowGap',
    'rubyAlign',
    'rubyMerge',
    'rubyPosition',
    'scale',
    'scrollBehavior',
    'scrollMargin',
    'scrollMarginBlock',
    'scrollMarginBlockEnd',
    'scrollMarginBlockStart',
    'scrollMarginBottom',
    'scrollMarginInline',
    'scrollMarginInlineEnd',
    'scrollMarginInlineStart',
    'scrollMarginLeft',
    'scrollMarginRight',
    'scrollMarginTop',
    'scrollPadding',
    'scrollPaddingBlock',
    'scrollPaddingBlockEnd',
    'scrollPaddingBlockStart',
    'scrollPaddingBottom',
    'scrollPaddingInline',
    'scrollPaddingInlineEnd',
    'scrollPaddingInlineStart',
    'scrollPaddingLeft',
    'scrollPaddingRight',
    'scrollPaddingTop',
    'scrollSnapAlign',
    'scrollSnapStop',
    'scrollSnapType',
    'scrollbarColor',
    'scrollbarWidth',
    'shapeImageThreshold',
    'shapeMargin',
    'shapeOutside',
    'tabSize',
    'tableLayout',
    'textAlign',
    'textAlignLast',
    'textCombineUpright',
    'textDecorationColor',
    'textDecorationLine',
    'textDecorationSkip',
    'textDecorationSkipInk',
    'textDecorationStyle',
    'textDecorationThickness',
    'textDecorationWidth',
    'textEmphasisColor',
    'textEmphasisPosition',
    'textEmphasisStyle',
    'textIndent',
    'textJustify',
    'textOrientation',
    'textOverflow',
    'textRendering',
    'textShadow',
    'textSizeAdjust',
    'textTransform',
    'textUnderlineOffset',
    'textUnderlinePosition',
    'top',
    'touchAction',
    'transform',
    'transformBox',
    'transformOrigin',
    'transformStyle',
    'transitionDelay',
    'transitionDuration',
    'transitionProperty',
    'transitionTimingFunction',
    'translate',
    'unicodeBidi',
    'userSelect',
    'verticalAlign',
    'visibility',
    'whiteSpace',
    'widows',
    'width',
    'willChange',
    'wordBreak',
    'wordSpacing',
    'wordWrap',
    'writingMode',
    'zIndex',
    'zoom',
];

const standardShorthandProperties = [
    'all',
    'animation',
    'background',
    'backgroundPosition',
    'border',
    'borderBlock',
    'borderBlockEnd',
    'borderBlockStart',
    'borderBottom',
    'borderColor',
    'borderImage',
    'borderInline',
    'borderInlineEnd',
    'borderInlineStart',
    'borderLeft',
    'borderRadius',
    'borderRight',
    'borderStyle',
    'borderTop',
    'borderWidth',
    'columnRule',
    'columns',
    'flex',
    'flexFlow',
    'font',
    'gap',
    'grid',
    'gridArea',
    'gridColumn',
    'gridRow',
    'gridTemplate',
    'lineClamp',
    'listStyle',
    'margin',
    'mask',
    'maskBorder',
    'motion',
    'offset',
    'outline',
    'overflow',
    'overscrollBehavior',
    'padding',
    'placeItems',
    'placeSelf',
    'textDecoration',
    'textEmphasis',
    'transition',
];

const standardProperties = [
    ...standardLonghandProperties,
    ...standardShorthandProperties,
];
const cssProperties = [...standardProperties];

const cssSpacingProperties = [
    'margin',
    'marginTop',
    'marginBottom',
    'marginLeft',
    'marginRight',
    'padding',
    'paddingTop',
    'paddingBottom',
    'paddingLeft',
    'paddingRight',
    'gap',
    'rowGap',
    'columnGap',
    'gridGap',
    'gridRowGap',
    'gridColumnGap',
];

const addStyleProp = (style, key, value, theme) => {
    var _a, _b;
    const returnStyle = style || {};
    if (cssColorProperties.includes(key)) {
        returnStyle[key] = ((_a = theme === null || theme === void 0 ? void 0 : theme.colors) === null || _a === void 0 ? void 0 : _a[value]) || value;
        return returnStyle;
    }
    if (cssSpacingProperties.includes(key)) {
        returnStyle[key] = ((_b = theme === null || theme === void 0 ? void 0 : theme.spacings) === null || _b === void 0 ? void 0 : _b[value]) || value;
        return returnStyle;
    }
    returnStyle[key] = value;
    return returnStyle;
};
const UiStyledElement = styled.div.withConfig({
    shouldForwardProp: (prop) => {
        if (prop === 'width' || prop === 'height') {
            return true;
        }
        if (cssProperties.includes(prop) || prop === 'csso' || prop === 'cssWidth' || prop === 'cssHeight') {
            return false;
        }
        return true;
    },
})((_a) => {
    var { csso, theme } = _a, props = __rest(_a, ["csso", "theme"]);
    let baseStyle = csso ? JSON.parse(JSON.stringify(csso)) : {};
    let isMediaQuery = false;
    props.width = props.cssWidth || props.width;
    props.height = props.cssHeight || props.height;
    Object.keys(props).forEach((propKey) => {
        if (cssProperties.includes(propKey)) {
            const propValue = props[propKey];
            if (typeof propValue == 'object') {
                Object.keys(propValue).forEach((propObjectKey) => {
                    var _a, _b;
                    const propObjectValue = propValue[propObjectKey];
                    if (propObjectKey === '_') {
                        baseStyle = addStyleProp(baseStyle, propKey, propObjectValue, theme);
                    }
                    else if (((_a = theme === null || theme === void 0 ? void 0 : theme.screens) === null || _a === void 0 ? void 0 : _a[propObjectKey]) !== undefined) {
                        const screenValue = (_b = theme === null || theme === void 0 ? void 0 : theme.screens) === null || _b === void 0 ? void 0 : _b[propObjectKey];
                        if (!screenValue || screenValue === '0' || screenValue === '0px') {
                            if (typeof propObjectValue == 'object') {
                                Object.keys(propObjectValue).forEach((propSubObjectKey) => {
                                    const propSubObjectValue = propObjectValue[propSubObjectKey];
                                    if (propSubObjectKey === '_') {
                                        baseStyle = addStyleProp(baseStyle, propKey, propSubObjectValue, theme);
                                    }
                                    else if (propSubObjectKey.startsWith(':')) {
                                        const pseudoProp = `&${propSubObjectKey}`;
                                        baseStyle[pseudoProp] = addStyleProp(baseStyle[pseudoProp], propKey, propSubObjectValue, theme);
                                    }
                                });
                            }
                            else {
                                baseStyle = addStyleProp(baseStyle, propKey, propObjectValue, theme);
                            }
                        }
                        else {
                            Object.values(theme.screens).forEach((themeScreenValue) => {
                                const mediaQuery = `@media (min-width: ${themeScreenValue})`;
                                if (!baseStyle[mediaQuery]) {
                                    baseStyle[mediaQuery] = {};
                                }
                            });
                            isMediaQuery = true;
                            const mediaQueryProp = `@media (min-width: ${screenValue})`;
                            let mediaQueryStyle = baseStyle[mediaQueryProp] || {};
                            if (typeof propObjectValue == 'object') {
                                Object.keys(propObjectValue).forEach((propSubObjectKey) => {
                                    const propSubObjectValue = propObjectValue[propSubObjectKey];
                                    if (propSubObjectKey === '_') {
                                        mediaQueryStyle = addStyleProp(mediaQueryStyle, propKey, propSubObjectValue, theme);
                                    }
                                    else if (propSubObjectKey.startsWith(':')) {
                                        const pseudoProp = `&${propSubObjectKey}`;
                                        mediaQueryStyle[pseudoProp] = addStyleProp(mediaQueryStyle[pseudoProp], propKey, propSubObjectValue, theme);
                                    }
                                });
                                baseStyle[mediaQueryProp] = mediaQueryStyle;
                            }
                            else {
                                baseStyle[mediaQueryProp] = addStyleProp(baseStyle[mediaQueryProp], propKey, propObjectValue, theme);
                            }
                        }
                    }
                    else if (propObjectKey.startsWith(':')) {
                        const pseudoProp = `&${propObjectKey}`;
                        baseStyle[pseudoProp] = addStyleProp(baseStyle[pseudoProp], propKey, propObjectValue, theme);
                    }
                });
            }
            else {
                baseStyle = addStyleProp(baseStyle, propKey, propValue, theme);
            }
        }
    });
    if (isMediaQuery) {
        Object.values(theme.screens).forEach((screenValue) => {
            const mediaQueryProp = `@media (min-width: ${screenValue})`;
            if (Object.keys(baseStyle[mediaQueryProp]).length === 0) {
                delete baseStyle[mediaQueryProp];
            }
        });
    }
    return baseStyle;
});

const UiElement = (_a) => {
    var { as, innerRef, htmlWidth, htmlHeight, width, height } = _a, props = __rest(_a, ["as", "innerRef", "htmlWidth", "htmlHeight", "width", "height"]);
    return (jsx(UiStyledElement, Object.assign({ as: as, ref: innerRef, width: htmlWidth, height: htmlHeight, cssWidth: width, cssHeight: height }, props)));
};

const UiButton = (_a) => {
    var _b;
    var { as, textVariant } = _a, props = __rest(_a, ["as", "textVariant"]);
    const theme = useTheme();
    return (jsx(UiElement, Object.assign({ as: as || 'button', csso: textVariant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[textVariant] : undefined, appearance: "none", type: "button", margin: 0 }, props)));
};

const UiDiv = (_a) => {
    var { as } = _a, props = __rest(_a, ["as"]);
    return jsx(UiElement, Object.assign({ as: as || 'div' }, props));
};

const UiFlex = (_a) => {
    var { as } = _a, props = __rest(_a, ["as"]);
    return jsx(UiElement, Object.assign({ as: as || 'div', display: "flex" }, props));
};

const UiImg = (_a) => {
    var { as } = _a, props = __rest(_a, ["as"]);
    return (jsx(UiElement, Object.assign({ as: as || 'img', htmlWidth: typeof props.width !== 'object' ? props.width : undefined, htmlHeight: typeof props.height !== 'object' ? props.height : undefined }, props)));
};

const UiInput = (_a) => {
    var { as } = _a, props = __rest(_a, ["as"]);
    return jsx(UiElement, Object.assign({ as: as || 'input' }, props));
};

const UiLink = (_a) => {
    var _b;
    var { as, textVariant } = _a, props = __rest(_a, ["as", "textVariant"]);
    const theme = useTheme();
    return jsx(UiElement, Object.assign({ as: as || 'a', csso: textVariant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[textVariant] : undefined }, props));
};

const UiText = (_a) => {
    var _b;
    var { variant, as } = _a, props = __rest(_a, ["variant", "as"]);
    const theme = useTheme();
    return jsx(UiElement, Object.assign({ as: as || 'div', csso: variant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[variant] : undefined }, props));
};

const UiSpan = (_a) => {
    var _b;
    var { as, textVariant } = _a, props = __rest(_a, ["as", "textVariant"]);
    const theme = useTheme();
    return jsx(UiElement, Object.assign({ as: as || 'span', csso: textVariant ? (_b = theme === null || theme === void 0 ? void 0 : theme.textVariants) === null || _b === void 0 ? void 0 : _b[textVariant] : undefined }, props));
};

const Block = (_a) => {
    var { display, boxSizing, justify, justifyContent, alignItems, children, theme } = _a, props = __rest(_a, ["display", "boxSizing", "justify", "justifyContent", "alignItems", "children", "theme"]);
    const isFlexProp = !!justify || !!justifyContent || !!alignItems;
    return (jsx(UiDiv, Object.assign({ display: display || isFlexProp ? 'flex' : undefined, boxSizing: boxSizing || 'border-box', justifyContent: justifyContent || justify, alignItems: alignItems, csso: theme === null || theme === void 0 ? void 0 : theme.root }, props, { children: children })));
};

const COLORS = {
    DARK: '#333333',
    LIGHT: '#f6f6f6',
    GREY: '#dddddd',
    GREY_HOVER: '#cccccc',
    GREY_ACTIVE: '#666666',
    GREY_TEXT: '#999999',
    PRIMARY: 'orange',
    PRIMARY_HOVER: 'darkorange',
    SECONDARY: 'skyblue',
    SECONDARY_HOVER: 'deepskyblue',
    dark: '#333333',
    light: '#f6f6f6',
    grey: '#dddddd',
    greyHover: '#cccccc',
    greyActive: '#666666',
    greyText: '#999999',
    primary: 'orange',
    primaryHover: 'darkorange',
    secondary: 'skyblue',
    secondaryHover: 'deepskyblue',
};
const SPACINGS = {
    XXS: '5px',
    XS: '10px',
    S: '20px',
    M: '30px',
    L: '50px',
    XL: '80px',
    XXL: '130px',
    xxs: '5px',
    xs: '10px',
    s: '20px',
    m: '30px',
    l: '50px',
    xl: '80px',
    xxl: '130px',
};
const TEXTSTYLE_SMALL = {
    fontFamily: '"Source Sans Pro", sans-serif',
    fontSize: '14px',
    lineHeight: '20px',
};
const TEXTSTYLE_MEDIUM = {
    fontFamily: '"Source Sans Pro", sans-serif',
    fontSize: '16px',
    lineHeight: '22px',
};
const COMMON_STYLES = {
    borderRadius: '3px',
    boxShadowDropdown: '0 1px 4px 0px rgba(0, 0, 0, 0.2)',
};

const styleDefault = Object.assign(Object.assign({ appearance: 'none', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', boxSizing: 'border-box', background: COLORS.GREY, border: 0, margin: 0, padding: '10px 15px' }, TEXTSTYLE_MEDIUM), { whiteSpace: 'nowrap', cursor: 'pointer', span: {
        display: 'inline-flex',
        '&:first-of-type': {
            marginLeft: 0,
        },
    }, svg: {
        width: '22px',
        height: '22px',
        verticalAlign: 'top',
    }, '&[data-size-small="true"]': Object.assign(Object.assign({ padding: '5px 12px' }, TEXTSTYLE_SMALL), { span: {
            display: 'inline-flex',
            '&:first-of-type': {
                marginLeft: 0,
            },
        }, svg: {
            width: '18px',
            height: '18px',
            verticalAlign: 'top',
        } }), '&[data-block="true"]': {
        display: 'flex',
        width: '100%',
    }, '&[data-disabled="true"]': {
        opacity: 0.5,
        cursor: 'default',
        pointerEvents: 'none',
    } });
const themeLayoutPrimary = {
    background: COLORS.PRIMARY,
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.PRIMARY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.PRIMARY,
        color: COLORS.DARK,
    },
};
const themeLayoutSecondary = {
    background: COLORS.SECONDARY,
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.SECONDARY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.SECONDARY,
        color: COLORS.DARK,
    },
};
const themeLayoutOutlined = {
    background: 'transparent',
    boxShadow: `inset 0px 0px 0px 1px ${COLORS.GREY}`,
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
        boxShadow: 'none',
    },
    '&:hover': {
        background: COLORS.LIGHT,
        color: COLORS.DARK,
        boxShadow: `inset 0px 0px 0px 1px ${COLORS.GREY_HOVER}`,
    },
    '&:active': {
        background: 'transparent',
        color: COLORS.DARK,
        boxShadow: `inset 0px 0px 0px 1px ${COLORS.GREY}`,
    },
};
const themeLayoutOption = {
    background: 'transparent',
    fontWeight: 400,
    '&[data-active="true"]': {
        background: 'transparent',
        color: COLORS.PRIMARY,
        '&:hover': {
            background: COLORS.LIGHT,
            color: COLORS.PRIMARY,
        },
        '&:active': {
            background: 'transparent',
            color: COLORS.PRIMARY,
        },
    },
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.LIGHT,
        color: COLORS.DARK,
    },
    '&:active': {
        background: 'transparent',
        color: COLORS.DARK,
    },
};
const themeLayoutDefault = {
    borderRadius: COMMON_STYLES.borderRadius,
    color: COLORS.DARK,
    fontWeight: 600,
    textDecoration: 'none',
    textAlign: 'left',
    span: {
        marginLeft: '7px',
    },
    '&:focus': {
        outline: 0,
        background: COLORS.DARK,
        color: 'white',
    },
    '&:hover': {
        background: COLORS.GREY_HOVER,
        color: COLORS.DARK,
    },
    '&:active': {
        background: COLORS.GREY,
        color: COLORS.DARK,
    },
    '&[data-with-icon="true"]': {
        padding: '10px 15px 10px 12px',
    },
    '&[data-with-icon-right="true"]': {
        padding: '10px 12px 10px 15px',
    },
    '&[data-with-icon-only="true"]': {
        padding: '10px',
    },
    '&[data-size-small="true"]': {
        span: {
            marginLeft: '5px',
        },
        '&[data-with-icon]': {
            padding: '5px 12px 5px 9px',
        },
        '&[data-with-icon-right]': {
            padding: '5px 9px 5px 12px',
        },
        '&[data-with-icon-only]': {
            padding: '6px',
        },
    },
    '&[data-layout-primary="true"]': themeLayoutPrimary,
    '&[data-layout-secondary="true"]': themeLayoutSecondary,
    '&[data-layout-outlined="true"]': themeLayoutOutlined,
    '&[data-layout-option="true"]': themeLayoutOption,
    '&[data-active="true"]': {
        background: COLORS.GREY_ACTIVE,
        color: 'white',
        boxShadow: 'none',
        '&:focus': {
            outline: 0,
            background: COLORS.DARK,
            color: 'white',
        },
        '&:hover': {
            background: COLORS.DARK,
            color: 'white',
            boxShadow: 'none',
        },
        '&:active': {
            background: COLORS.GREY_ACTIVE,
            color: 'white',
            boxShadow: 'none',
        },
    },
};

const StyledButton = styled.button(({ jss }) => jss);
const StyledLink = styled.a(({ jss }) => jss);
const Button = ({ layout, size, type = 'button', label, href, target, title, disabled, icon, iconRight, block, justify = 'center', active, ariaLabel, className, style, theme, stopPropagation, onClick, onMouseUp, onMouseDown, }) => {
    const handleOnClick = (e) => {
        if (href && disabled) {
            e.preventDefault();
        }
        if (stopPropagation) {
            e.stopPropagation();
            e.nativeEvent.stopImmediatePropagation();
        }
        if (!disabled) {
            typeof onClick === 'function' && onClick(e);
        }
    };
    const handleOnMouseLeave = (e) => {
        e.currentTarget.blur();
    };
    const data = {
        'data-active': active || undefined,
        'data-disabled': disabled || undefined,
        'data-with-icon': Boolean(label && icon) || undefined,
        'data-with-icon-right': Boolean(label && iconRight) || undefined,
        'data-with-icon-only': Boolean(!label && icon) || undefined,
        [`data-size-${size}`]: !!size || undefined,
        [`data-layout-${layout}`]: !!layout || undefined,
        'data-block': block || undefined,
    };
    const rootClassName = className || 'ui-button';
    let jss = Object.assign({}, styleDefault);
    if (justify) {
        jss.justifyContent = justify;
    }
    const rootTheme = theme ? theme.root : null;
    if (rootTheme) {
        jss = deepmerge(jss, rootTheme);
    }
    else {
        jss = deepmerge(jss, themeLayoutDefault);
    }
    const settings = {
        title: title,
        'aria-label': ariaLabel,
        onClick: handleOnClick,
        onMouseLeave: handleOnMouseLeave,
        onMouseUp: onMouseUp,
        onMouseDown: onMouseDown,
    };
    const contents = (jsxs(Fragment, { children: [icon && jsx("span", Object.assign({ className: `${rootClassName}__icon` }, { children: icon })), label && jsx("span", Object.assign({ className: `${rootClassName}__label` }, { children: label })), iconRight && jsx("span", Object.assign({ className: `${rootClassName}__icon-right` }, { children: iconRight }))] }));
    if (href) {
        return (jsx(StyledLink, Object.assign({ href: href, target: (href ? target : undefined) }, settings, data, { jss: jss, style: style, className: rootClassName }, { children: contents })));
    }
    return (jsx(StyledButton, Object.assign({ type: (!href ? type : undefined), disabled: disabled }, settings, data, { jss: jss, style: style, className: rootClassName }, { children: contents })));
};

const StyledGroupItem = styled.div(({ jss }) => jss);
const GroupItemLegacy = ({ spacing, hSpacing, vSpacing, grow, width, minWidth, maxWidth, vertical, children, }) => {
    let paddingRight = undefined;
    let paddingBottom = undefined;
    if (SPACINGS[spacing]) {
        paddingRight = SPACINGS[spacing];
        paddingBottom = SPACINGS[spacing];
    }
    else if (spacing) {
        paddingRight = spacing;
        paddingBottom = spacing;
    }
    if (SPACINGS[hSpacing]) {
        paddingRight = SPACINGS[hSpacing];
    }
    else if (hSpacing) {
        paddingRight = hSpacing;
    }
    if (SPACINGS[vSpacing]) {
        paddingBottom = SPACINGS[vSpacing];
    }
    else if (vSpacing) {
        paddingBottom = vSpacing;
    }
    let flexGrow = undefined;
    if (!vertical && grow) {
        flexGrow = 1;
    }
    const jss = {
        width,
        minWidth,
        maxWidth,
        paddingRight,
        paddingBottom,
        flexGrow,
    };
    return jsx(StyledGroupItem, Object.assign({ jss: jss }, { children: children }));
};

const StyledWrapper$2 = styled.div(({ jss }) => jss);
const StyledGroup = styled.div(({ jss }) => jss);
const GroupLegacy = ({ spacing, hSpacing, vSpacing, justify, alignItems, grow = false, nowrap = false, vertical = false, width, background, padding, paddingTop, paddingLeft, paddingRight, paddingBottom, children, }) => {
    const aChildren = React.Children.toArray(children);
    const jssWrapper = {
        display: 'flex',
        boxSizing: 'border-box',
        width,
        padding: SPACINGS[padding] || padding,
        paddingTop: SPACINGS[paddingTop] || paddingTop,
        paddingLeft: SPACINGS[paddingLeft] || paddingLeft,
        paddingRight: SPACINGS[paddingRight] || paddingRight,
        paddingBottom: SPACINGS[paddingBottom] || paddingBottom,
        background: COLORS[background] || background,
    };
    let marginRight = undefined;
    let marginBottom = undefined;
    if (SPACINGS[spacing]) {
        marginRight = `-${SPACINGS[spacing]}`;
        marginBottom = `-${SPACINGS[spacing]}`;
    }
    else if (spacing) {
        marginRight = `-${spacing}`;
        marginBottom = `-${spacing}`;
    }
    if (SPACINGS[hSpacing]) {
        marginRight = `-${SPACINGS[hSpacing]}`;
    }
    else if (hSpacing) {
        marginRight = `-${hSpacing}`;
    }
    if (SPACINGS[vSpacing]) {
        marginBottom = `-${SPACINGS[vSpacing]}`;
    }
    else if (vSpacing) {
        marginBottom = `-${vSpacing}`;
    }
    let flexFlow = 'row wrap';
    if (!vertical && nowrap) {
        flexFlow = 'row nowrap';
    }
    let display = 'flex';
    if (vertical) {
        display = 'block';
    }
    const jssGroup = {
        flexGrow: 1,
        display,
        flexFlow,
        justifyContent: !vertical ? justify : undefined,
        alignItems: !vertical && alignItems ? alignItems : undefined,
        marginRight,
        marginBottom,
    };
    return (jsx(StyledWrapper$2, Object.assign({ jss: jssWrapper }, { children: jsx(StyledGroup, Object.assign({ jss: jssGroup }, { children: aChildren.map((child, i) => {
                if (!child) {
                    return false;
                }
                const childSettings = {
                    spacing: spacing,
                    hSpacing: hSpacing,
                    vSpacing: vSpacing,
                    grow,
                    vertical,
                };
                const childAsReactElement = child;
                if (childAsReactElement.type === GroupItemLegacy) {
                    return React.cloneElement(childAsReactElement, Object.assign(Object.assign({ key: String(i) }, childSettings), { grow: childAsReactElement.props.grow || grow }));
                }
                return (jsx(GroupItemLegacy, Object.assign({}, childSettings, { children: child }), String(i)));
            }) })) })));
};

const Check = (jsx("svg", Object.assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z" }) })));

const StyledRoot$1 = styled.div({
    position: 'relative',
    '&[data-disabled="true"]': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
});
const StyledInput$2 = styled.input({
    appearance: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    height: 0,
    width: 0,
    margin: 0,
    padding: 0,
    fontSize: 0,
    lineHeight: 0,
    opacity: 0,
});
const StyledCheckbox = styled.div(({ jss }) => jss);
const StyledLabel$1 = styled.div(({ jss }) => jss);
const Checkbox = ({ id, name, value, label, ariaLabel, checked = false, disabled = false, spacing = 'xs', icon, onChange, theme, }) => {
    const [isChecked, setIsChecked] = useState(checked);
    const [isFocus, setIsFocus] = useState(false);
    const refInput = useRef(null);
    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);
    const handleOnInputChange = (e) => {
        setIsChecked(e.target.checked);
        if (typeof onChange === 'function')
            onChange(e);
    };
    const handleOnClickCheckbox = () => {
        if (refInput && refInput.current) {
            refInput.current.click();
            refInput.current.focus();
        }
    };
    const handleOnFocus = () => {
        setIsFocus(true);
    };
    const handleOnBlur = () => {
        setIsFocus(false);
    };
    const data = {
        'data-focus': isFocus || undefined,
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
    };
    let jssCheckbox = {
        flex: '0 0 auto',
        width: '22px',
        height: '22px',
        border: `1px solid ${COLORS.GREY_TEXT}`,
        boxSizing: 'border-box',
        background: 'white',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        svg: {
            width: '18px',
            height: '18px',
            opacity: 0,
        },
        '&[data-checked="true"]': {
            svg: {
                opacity: 1,
            },
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.checkbox) {
        jssCheckbox = deepmerge(jssCheckbox, theme === null || theme === void 0 ? void 0 : theme.checkbox);
    }
    else {
        jssCheckbox = deepmerge(jssCheckbox, {
            color: COLORS.DARK,
            '&[data-focus="true"]': {
                borderColor: COLORS.DARK,
            },
            '&[data-checked="true"]': {
                svg: {
                    color: COLORS.PRIMARY,
                },
            },
        });
    }
    let jssLabel = Object.assign({ display: 'inline-block' }, TEXTSTYLE_MEDIUM);
    if (theme === null || theme === void 0 ? void 0 : theme.label) {
        jssLabel = deepmerge(jssLabel, theme.label);
    }
    return (jsxs(StyledRoot$1, Object.assign({}, data, { children: [jsx(StyledInput$2, { ref: refInput, id: id, type: "checkbox", name: name, value: value, checked: isChecked, "aria-label": (ariaLabel || label), disabled: disabled, onChange: handleOnInputChange, onFocus: handleOnFocus, onBlur: handleOnBlur }), jsxs(GroupLegacy, Object.assign({ nowrap: true, spacing: spacing }, { children: [jsx(StyledCheckbox, Object.assign({ jss: jssCheckbox, onClick: handleOnClickCheckbox }, data, { children: icon || Check })), label && (jsx(StyledLabel$1, Object.assign({ jss: jssLabel }, data, { children: label })))] }))] })));
};

const Collapse = ({ show = false, duration = 200, animationType = 'ease', fadeInOut = false, keepChildren = true, children, }) => {
    const [isShown, setIsShown] = useState(show);
    const [renderContent, setRenderContent] = useState(keepChildren || isShown);
    const [maxHeight, setMaxHeight] = useState(show ? 'none' : 0);
    const [overflow, setOverflow] = useState(show ? 'visible' : 'hidden');
    const [timeoutIdShow, setTimeoutIdShow] = useState(undefined);
    const [timeoutIdHide, setTimeoutIdHide] = useState(undefined);
    const refComponent = useRef(null);
    const refWrapper = useRef(null);
    useEffect(() => {
        var _a, _b;
        const componentHeight = ((_a = refComponent === null || refComponent === void 0 ? void 0 : refComponent.current) === null || _a === void 0 ? void 0 : _a.clientHeight) || 0;
        const wrapperHeight = ((_b = refWrapper === null || refWrapper === void 0 ? void 0 : refWrapper.current) === null || _b === void 0 ? void 0 : _b.clientHeight) || 0;
        if (!keepChildren && isShown && !renderContent) {
            setRenderContent(true);
        }
        if (isShown && renderContent) {
            if (componentHeight === wrapperHeight) {
                setMaxHeight('none');
                setOverflow('visible');
            }
            if (componentHeight !== wrapperHeight) {
                setMaxHeight(`${wrapperHeight}px`);
                setOverflow('hidden');
                setTimeoutIdShow(window.setTimeout(() => {
                    setMaxHeight('none');
                    setOverflow('visible');
                }, duration));
            }
        }
        if (!isShown) {
            if (componentHeight === 0) {
                setMaxHeight(0);
                setOverflow('hidden');
            }
            if (componentHeight > 0) {
                setMaxHeight(`${wrapperHeight}px`);
                setTimeoutIdHide(window.setTimeout(() => {
                    setMaxHeight(0);
                    setOverflow('hidden');
                    if (!keepChildren) {
                        setTimeoutIdShow(window.setTimeout(() => {
                            setRenderContent(false);
                        }, duration));
                    }
                }, 50));
            }
        }
    }, [isShown, renderContent, duration, keepChildren]);
    useEffect(() => {
        setIsShown(show);
    }, [show]);
    useEffect(() => {
        return function cleanup() {
            window.clearTimeout(timeoutIdShow);
            window.clearTimeout(timeoutIdHide);
        };
    }, [timeoutIdShow, timeoutIdHide]);
    const style = {
        maxHeight,
        overflow,
        opacity: !show && fadeInOut ? 0 : 1,
        transition: `max-height ${duration / 1000}s ${animationType}, opacity ${duration / 1000}s ease`,
    };
    return (jsx("div", Object.assign({ style: style, ref: refComponent }, { children: jsx("div", Object.assign({ ref: refWrapper }, { children: renderContent && children })) })));
};

const useResizeObserver = ({ disabled, element, onResize }) => {
    const current = element === null || element === void 0 ? void 0 : element.current;
    const observer = useRef(null);
    useEffect(() => {
        if (!disabled) {
            const observe = () => {
                if (element && element.current && observer && observer.current) {
                    observer.current.observe(element === null || element === void 0 ? void 0 : element.current);
                }
            };
            if (observer && observer.current && current) {
                observer.current.unobserve(current);
            }
            observer.current = new ResizeObserver(onResize);
            observe();
        }
        return () => {
            if (observer && observer.current && element && current) {
                observer.current.unobserve(current);
            }
        };
    }, [disabled, current, element, onResize]);
};

const StyledComponent$1 = styled.div({
    position: 'relative',
    paddingTop: '100%',
    overflow: 'hidden',
});
const StyledWrapper$1 = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
});
const StyledImage$1 = styled.img(({ fadeIn = false, legacyMode = false }) => {
    return {
        position: 'absolute',
        top: legacyMode ? '-1000%' : 0,
        bottom: legacyMode ? '-1000%' : 0,
        left: legacyMode ? '-1000%' : 0,
        right: legacyMode ? '-1000%' : 0,
        margin: 'auto',
        verticalAlign: 'top',
        width: '100%',
        height: '100%',
        objectFit: !legacyMode ? 'cover' : 'fill',
        opacity: fadeIn ? 0 : 1,
        transition: fadeIn ? 'opacity 0.3s ease' : '',
        '&[data-loaded="true"]': {
            opacity: 1,
        },
    };
});
const CroppedImage = (props) => {
    const { src, srcWebP, alt, ratio, width, height, background, srcSet, sizes, fadeIn = false, legacyMode = false, children, onImageLoaded, onImageResize, } = props;
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [naturalWidth, setNaturalWidth] = useState(0);
    const [naturalHeight, setNaturalHeight] = useState(0);
    const [componentWidth, setComponentWidth] = useState(0);
    const [componentHeight, setComponentHeight] = useState(0);
    const wrapperRef = useRef(null);
    const imgRef = useRef(null);
    const _ratio = ratio || '1:1';
    const [w, h] = _ratio.split(':');
    const ratioWidth = parseFloat(w) || 1;
    const ratioHeight = parseFloat(h) || 1;
    useResizeObserver({
        element: wrapperRef,
        onResize: (resizeObserverEntries) => {
            if (legacyMode) {
                const entry = resizeObserverEntries[0];
                if (entry) {
                    const contentRect = entry.contentRect || {};
                    setComponentWidth(contentRect.width);
                    setComponentHeight(contentRect.height);
                }
            }
            if (imgRef && imgRef.current) {
                typeof onImageResize === 'function' && onImageResize(imgRef.current.offsetWidth, imgRef.current.offsetHeight);
            }
        },
    });
    const handleOnImageLoaded = () => {
        if (legacyMode && imgRef.current) {
            setNaturalWidth(imgRef.current.naturalWidth || 1);
            setNaturalHeight(imgRef.current.naturalHeight || 1);
        }
        setIsImageLoaded(true);
        typeof onImageLoaded === 'function' && onImageLoaded();
    };
    const componentStyle = {
        width: 'auto',
        height: 'auto',
        paddingTop: `${(100 / ratioWidth) * ratioHeight}%`,
        background,
    };
    if (width) {
        componentStyle.width = width;
    }
    if (height) {
        componentStyle.height = height;
        componentStyle.paddingTop = '0px';
    }
    if (width && !height && ratio) {
        componentStyle.paddingTop = `calc(${width} / ${ratioWidth / ratioHeight})`;
    }
    if (!width && height && ratio) {
        componentStyle.width = `calc(${height} * ${ratioWidth / ratioHeight})`;
    }
    let imageWidth = '100%';
    let imageHeight = '100%';
    if (legacyMode) {
        let componentRatioFloat = 1;
        if (ratio) {
            componentRatioFloat = ratioWidth / ratioHeight;
        }
        else if (componentWidth && componentHeight) {
            componentRatioFloat = componentWidth / componentHeight;
        }
        else if (wrapperRef.current) {
            componentRatioFloat = wrapperRef.current.offsetWidth / wrapperRef.current.offsetHeight;
        }
        if (componentRatioFloat > naturalWidth / naturalHeight) {
            imageWidth = '100%';
            imageHeight = 'auto';
        }
        else {
            imageWidth = 'auto';
            imageHeight = '100%';
        }
    }
    const imageStyle = {
        width: imageWidth,
        height: imageHeight,
    };
    const data = {
        'data-loaded': isImageLoaded || false,
    };
    const image = src ? (jsx(StyledImage$1, Object.assign({ ref: imgRef, src: src, srcSet: srcSet, sizes: sizes, alt: alt, width: imageWidth, height: imageHeight, fadeIn: fadeIn, legacyMode: legacyMode, onLoad: handleOnImageLoaded, style: imageStyle }, data))) : null;
    return (jsx(StyledComponent$1, Object.assign({ style: componentStyle }, data, { children: jsx(StyledWrapper$1, Object.assign({ ref: wrapperRef }, { children: children ? (jsxs("picture", { children: [children, image] })) : (jsx(Fragment, { children: srcWebP ? (jsxs("picture", { children: [jsx("source", { srcSet: srcWebP, type: "image/webp" }), image] })) : (image) })) })) })));
};

const ChevronUp = (jsx("svg", Object.assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M7.41,15.41L12,10.83L16.59,15.41L18,14L12,8L6,14L7.41,15.41Z" }) })));

const ChevronDown = (jsx("svg", Object.assign({ viewBox: "0 0 24 24" }, { children: jsx("path", { fill: "currentColor", d: "M7.41,8.58L12,13.17L16.59,8.58L18,10L12,16L6,10L7.41,8.58Z" }) })));

const StyledDropdown = styled.div(({ jss }) => jss);
const StyledMenuWrapper$1 = styled.div({
    position: 'relative',
    width: '100%',
    height: 0,
});
const StyledMenu$1 = styled.div(({ jss }) => jss);
const Dropdown = ({ layout, label, block = false, iconOpen, iconClose, disabled = false, show = false, children, onChange, theme, }) => {
    const [isOpen, setIsOpen] = useState(false);
    const refComponent = useRef(null);
    const handleOnClickDocument = (e) => {
        var _a;
        if (!((_a = refComponent === null || refComponent === void 0 ? void 0 : refComponent.current) === null || _a === void 0 ? void 0 : _a.contains(e.target))) {
            setIsOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleOnClickDocument);
        return () => {
            document.removeEventListener('click', handleOnClickDocument);
        };
    }, []);
    useEffect(() => {
        if (typeof onChange === 'function')
            onChange(isOpen);
    }, [isOpen]);
    useEffect(() => {
        setIsOpen(show);
    }, [show]);
    const handleOnClickButton = () => {
        setIsOpen(!isOpen);
    };
    const _iconOpen = iconOpen || ChevronDown;
    const _iconClose = iconClose || ChevronUp;
    let jssDropdown = {
        position: 'relative',
        display: block ? 'block' : 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssDropdown = deepmerge(jssDropdown, theme === null || theme === void 0 ? void 0 : theme.root);
    }
    let jssMenu = {
        position: 'absolute',
        zIndex: 10,
        top: 0,
        left: 0,
        minWidth: '100%',
        boxSizing: 'border-box',
        background: 'white',
        boxShadow: COMMON_STYLES.boxShadowDropdown,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.menu) {
        jssMenu = deepmerge(jssMenu, theme === null || theme === void 0 ? void 0 : theme.menu);
    }
    else {
        jssMenu = deepmerge(jssMenu, {
            padding: '5px',
            borderRadius: COMMON_STYLES.borderRadius,
        });
    }
    return (jsxs(StyledDropdown, Object.assign({ ref: refComponent, jss: jssDropdown }, { children: [jsx(Button, { layout: layout, label: label, iconRight: isOpen ? _iconClose : _iconOpen, onClick: handleOnClickButton, block: block, justify: "space-between", disabled: disabled, theme: {
                    root: theme === null || theme === void 0 ? void 0 : theme.button,
                } }), isOpen && (jsx(StyledMenuWrapper$1, { children: jsx(StyledMenu$1, Object.assign({ jss: jssMenu }, { children: children })) }))] })));
};

const StyledGridItem = styled.div(({ jss }) => jss);
const StyledGridItemCell = styled.div(({ jss }) => jss);
const GridItem = ({ grow = false, width, background, gridSpacing, gridHSpacing, gridVSpacing, gridNowrap = false, children, theme, }) => {
    let paddingRight = undefined;
    let paddingBottom = undefined;
    if (SPACINGS[gridSpacing]) {
        paddingRight = SPACINGS[gridSpacing];
        paddingBottom = SPACINGS[gridSpacing];
    }
    else if (gridSpacing) {
        paddingRight = gridSpacing;
        paddingBottom = gridSpacing;
    }
    if (SPACINGS[gridHSpacing]) {
        paddingRight = SPACINGS[gridHSpacing];
    }
    else if (gridHSpacing) {
        paddingRight = gridHSpacing;
    }
    if (SPACINGS[gridVSpacing]) {
        paddingBottom = SPACINGS[gridVSpacing];
    }
    else if (gridVSpacing) {
        paddingBottom = gridVSpacing;
    }
    if (gridNowrap) {
        paddingBottom = 0;
    }
    const jssItem = {
        width,
        flexBasis: width,
        flexGrow: grow ? 1 : 0,
        flexShrink: 0,
        boxSizing: 'border-box',
        display: 'flex',
        paddingRight,
        paddingBottom,
    };
    let jssItemCell = {
        flexGrow: 1,
        background: COLORS[background] || background,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssItemCell = Object.assign(Object.assign({}, jssItemCell), theme === null || theme === void 0 ? void 0 : theme.root);
    }
    return (jsx(StyledGridItem, Object.assign({ jss: jssItem }, { children: jsx(StyledGridItemCell, Object.assign({ jss: jssItemCell }, { children: children })) })));
};

const StyledGridWrapper = styled.div(({ jss }) => jss);
const StyledGrid = styled.div(({ jss }) => jss);
const Grid = ({ columns = 12, spacing, hSpacing, vSpacing, justifyContent, justify, alignItems, alignContent, nowrap = false, children, theme, }) => {
    const aChildren = React.Children.toArray(children);
    let jssWrapper = {
        display: 'flex',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssWrapper = deepmerge(jssWrapper, theme === null || theme === void 0 ? void 0 : theme.root);
    }
    let marginRight = undefined;
    let marginBottom = undefined;
    if (SPACINGS[spacing]) {
        marginRight = `-${SPACINGS[spacing]}`;
        marginBottom = `-${SPACINGS[spacing]}`;
    }
    else if (spacing) {
        marginRight = `-${spacing}`;
        marginBottom = `-${spacing}`;
    }
    if (SPACINGS[hSpacing]) {
        marginRight = `-${SPACINGS[hSpacing]}`;
    }
    else if (hSpacing) {
        marginRight = `-${hSpacing}`;
    }
    if (SPACINGS[vSpacing]) {
        marginBottom = `-${SPACINGS[vSpacing]}`;
    }
    else if (vSpacing) {
        marginBottom = `-${vSpacing}`;
    }
    const jssGrid = {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: justifyContent || justify,
        alignItems,
        alignContent,
        marginRight: marginRight,
        marginBottom: marginBottom,
    };
    if (nowrap) {
        jssGrid.flexWrap = 'nowrap';
        jssGrid.marginBottom = undefined;
    }
    return (jsx(StyledGridWrapper, Object.assign({ jss: jssWrapper }, { children: jsx(StyledGrid, Object.assign({ jss: jssGrid }, { children: aChildren.map((child, i) => {
                var _a;
                const childAsReactElement = child;
                if (childAsReactElement.type === GridItem) {
                    const childProps = childAsReactElement.props || {};
                    let width = childProps.width;
                    if (!childProps.width || childProps.width === 'auto') {
                        if (childProps.grow) {
                            width = '0%';
                        }
                        else {
                            const colspan = childProps.cols || childProps.colspan || 1;
                            width = `${(100 / parseFloat(String(columns))) * parseFloat(colspan)}%`;
                        }
                    }
                    return React.cloneElement(childAsReactElement, {
                        key: String(i),
                        width,
                        gridSpacing: spacing,
                        gridHSpacing: hSpacing,
                        gridVSpacing: vSpacing,
                        gridNowrap: nowrap,
                        theme: {
                            root: deepmerge((theme === null || theme === void 0 ? void 0 : theme.item) || {}, ((_a = childProps === null || childProps === void 0 ? void 0 : childProps.theme) === null || _a === void 0 ? void 0 : _a.root) || {}),
                        },
                    });
                }
                return null;
            }) })) })));
};

const Group = (_a) => {
    var { display, flexDirection, vertical, flexWrap, nowrap, gap, spacing, columnGap, hSpacing, rowGap, vSpacing, justify, justifyContent, children } = _a, props = __rest(_a, ["display", "flexDirection", "vertical", "flexWrap", "nowrap", "gap", "spacing", "columnGap", "hSpacing", "rowGap", "vSpacing", "justify", "justifyContent", "children"]);
    return (jsx(UiDiv, Object.assign({ display: display || 'flex', flexDirection: flexDirection || vertical ? 'column' : undefined, flexWrap: flexWrap || nowrap ? 'nowrap' : 'wrap', gap: gap || spacing, columnGap: columnGap || hSpacing, rowGap: rowGap || vSpacing, justifyContent: justifyContent || justify }, props, { children: children })));
};

const GroupItem = (_a) => {
    var { grow, flexGrow, children } = _a, props = __rest(_a, ["grow", "flexGrow", "children"]);
    return (jsx(UiDiv, Object.assign({ flexGrow: flexGrow || grow ? 1 : undefined }, props, { children: children })));
};

const StyledRatioBox = styled.div({
    position: 'relative',
});
const StyledRatioInnerBox = styled.div({
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
});
const RatioBox = ({ ratio, width, height, background, children }) => {
    const _ratio = ratio || '1:1';
    const [w, h] = _ratio.split(':');
    const ratioWidth = parseFloat(w) || 1;
    const ratioHeight = parseFloat(h) || 1;
    const ratioFloat = ratioWidth / ratioHeight;
    const inlineStyle = {
        width,
        height,
        paddingTop: ratio && !height ? `${(100 / ratioWidth) * ratioHeight}%` : undefined,
        background,
    };
    if (width && !height && ratio) {
        inlineStyle.paddingTop = `calc(${width} / ${ratioFloat})`;
    }
    if (!width && height && ratio) {
        inlineStyle.width = `calc(${height} * ${ratioFloat})`;
    }
    return (jsx(StyledRatioBox, Object.assign({ style: inlineStyle }, { children: jsx(StyledRatioInnerBox, { children: children }) })));
};

const ImageSource = () => {
    return null;
};
var ImageSource$1 = React.memo(ImageSource);

const useImage = ({ imageElementRef, children, }) => {
    const observerRef = useRef(null);
    const [persistedImageSources, setPersistedImageSources] = useState(undefined);
    const [matchedImageSource, setMatchedImageSource] = useState(undefined);
    const [isImageSourceWebP, setIsImageSourceWebP] = useState(false);
    const setImageSources = (imageSources) => {
        if (JSON.stringify(imageSources) !== JSON.stringify(persistedImageSources)) {
            setPersistedImageSources(imageSources);
        }
    };
    useEffect(() => {
        if (children) {
            let isWebP = false;
            const imageSources = React.Children.map(children, (child) => {
                const childAsReactElement = child;
                if (childAsReactElement.type === ImageSource$1) {
                    const childProps = childAsReactElement.props;
                    if (childProps.srcWebP) {
                        isWebP = true;
                    }
                    return childProps;
                }
                return null;
            });
            setIsImageSourceWebP(isWebP);
            if (imageSources && imageSources.length > 0) {
                setImageSources(imageSources);
            }
            else {
                setImageSources(undefined);
            }
        }
        else {
            setImageSources(undefined);
        }
    }, [children]);
    useEffect(() => {
        const handleResizeObserverCallback = (entries) => {
            var _a, _b;
            if ((imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current) && entries[0]) {
                const imageWidth = ((_a = entries[0].contentRect) === null || _a === void 0 ? void 0 : _a.width) || (imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current.offsetWidth);
                const imageHeight = ((_b = entries[0].contentRect) === null || _b === void 0 ? void 0 : _b.height) || (imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current.offsetHeight);
                setMatchedImageSource(persistedImageSources === null || persistedImageSources === void 0 ? void 0 : persistedImageSources.find((imageSource) => {
                    const { minWidth, minHeight } = imageSource;
                    return ((minWidth !== undefined ? imageWidth >= minWidth : true) &&
                        (minHeight !== undefined ? imageHeight >= minHeight : true));
                }));
            }
        };
        if ((imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current) && persistedImageSources && persistedImageSources.length > 0) {
            if (observerRef.current) {
                observerRef.current.unobserve(imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current);
            }
            if (ResizeObserver) {
                observerRef.current = new ResizeObserver(handleResizeObserverCallback);
                observerRef.current.observe(imageElementRef === null || imageElementRef === void 0 ? void 0 : imageElementRef.current);
            }
        }
    }, [imageElementRef, persistedImageSources]);
    return {
        areImageSourcesAvailable: !!persistedImageSources,
        matchedImageSource,
        isImageSourceWebP,
    };
};

const StyledImage = styled.img(({ styledCSSObject }) => styledCSSObject);
const Image = ({ src, srcWebP, alt, display, width, minWidth, maxWidth, height, minHeight, maxHeight, ratio, verticalAlign = 'top', objectFit, objectPosition, srcSet, sizes, background, transition, children, onImageLoaded, }) => {
    const imageElementRef = useRef(null);
    const { areImageSourcesAvailable, matchedImageSource, isImageSourceWebP } = useImage({
        imageElementRef,
        children,
    });
    const handleOnImageLoaded = (e) => {
        if (typeof onImageLoaded === 'function')
            onImageLoaded(e);
    };
    const styledCSSObject = {
        display,
        width: ratio ? '100%' : width,
        minWidth: ratio ? '100%' : minWidth,
        maxWidth: ratio ? '100%' : maxWidth,
        height: ratio ? '100%' : height,
        minHeight: ratio ? '100%' : minHeight,
        maxHeight: ratio ? '100%' : maxHeight,
        verticalAlign,
        objectFit: objectFit || (ratio ? 'cover' : undefined),
        objectPosition,
        background,
        transition,
    };
    const image = src ? (jsx(StyledImage, { ref: imageElementRef, src: (matchedImageSource === null || matchedImageSource === void 0 ? void 0 : matchedImageSource.src) || src, alt: alt, width: width, height: height, srcSet: srcSet, sizes: sizes, styledCSSObject: styledCSSObject, onLoad: handleOnImageLoaded })) : null;
    let imageComponent;
    if (areImageSourcesAvailable && isImageSourceWebP) {
        imageComponent = (jsxs("picture", { children: [jsx("source", { srcSet: (matchedImageSource === null || matchedImageSource === void 0 ? void 0 : matchedImageSource.srcWebP) || srcWebP, type: "image/webp" }), image] }));
    }
    else if (areImageSourcesAvailable) {
        imageComponent = image;
    }
    else if (srcWebP) {
        imageComponent = (jsxs("picture", { children: [jsx("source", { srcSet: srcWebP, type: "image/webp" }), image] }));
    }
    else if (children) {
        imageComponent = (jsxs("picture", { children: [children, image] }));
    }
    else {
        imageComponent = image;
    }
    if (ratio) {
        return (jsx(RatioBox, Object.assign({ ratio: ratio, width: width, height: height, background: background }, { children: imageComponent })));
    }
    return imageComponent;
};

const StyledWrapper = styled.span(({ jss }) => jss);
const InlineSVG = ({ width = '24px', height = 'auto', color, children }) => {
    const jss = {
        display: 'inline-block',
        width,
        height,
        color: COLORS[color] || color,
        svg: {
            width: '100%',
            height: '100%',
            verticalAlign: 'top',
        },
    };
    return jsx(StyledWrapper, Object.assign({ jss: jss }, { children: children }));
};

const Input = (_a) => {
    var { type, value, width, block, useDefaultStyle, theme, ariaLabel, blurOnEnter, onChange, onKeyPress, onEnter } = _a, props = __rest(_a, ["type", "value", "width", "block", "useDefaultStyle", "theme", "ariaLabel", "blurOnEnter", "onChange", "onKeyPress", "onEnter"]);
    const styledTheme = useTheme();
    const [draftValue, setDraftValue] = useState(value);
    const handleOnKeyPress = (e) => {
        var _a;
        if (onKeyPress) {
            onKeyPress(e);
        }
        if (onEnter && (e.code === 'Enter' || e.key === 'Enter')) {
            onEnter(e);
            if (blurOnEnter) {
                (_a = e.currentTarget) === null || _a === void 0 ? void 0 : _a.blur();
            }
        }
    };
    const defaultStyle = useDefaultStyle && !!styledTheme
        ? Object.assign({ appearance: 'none', boxSizing: 'border-box', background: 'white', padding: '10px 15px', boxShadow: `inset 0px 0px 0px 1px ${styledTheme.colors.grey}`, border: 0, borderRadius: '3px', fontFamily: '"Source Sans Pro", sans-serif', fontSize: '16px', lineHeight: '22px', color: styledTheme.colors.dark, '&::placeholder': {
                fontStyle: 'italic',
                color: styledTheme.colors.greyText,
            }, '&:hover': {
                boxShadow: `inset 0px 0px 0px 1px ${styledTheme.colors.greyHover}`,
            }, '&:focus': {
                outline: 0,
                boxShadow: `inset 0px 0px 0px 2px ${styledTheme.colors.primary}`,
            }, '&:disabled': {
                opacity: 0.5,
                cursor: 'default',
                pointerEvents: 'none',
            } }, theme === null || theme === void 0 ? void 0 : theme.root) : undefined;
    const csso = !!defaultStyle && (theme === null || theme === void 0 ? void 0 : theme.root) ? deepmerge(defaultStyle, theme === null || theme === void 0 ? void 0 : theme.root) : defaultStyle || (theme === null || theme === void 0 ? void 0 : theme.root);
    return (jsx(UiInput, Object.assign({ type: type || 'text', value: draftValue || '', width: block ? '100%' : width, csso: csso, "aria-label": ariaLabel, onChange: (e) => {
            setDraftValue(e.target.value);
            if (onChange) {
                onChange(e);
            }
        }, onKeyPress: handleOnKeyPress }, props)));
};

const StyledRoot = styled.div({
    position: 'relative',
    '&[data-disabled="true"]': {
        opacity: 0.5,
        pointerEvents: 'none',
    },
});
const StyledRadio = styled.div(({ jss }) => jss);
const StyledInput$1 = styled.input({
    appearance: 'none',
    position: 'absolute',
    top: 0,
    left: 0,
    height: 0,
    width: 0,
    margin: 0,
    padding: 0,
    fontSize: 0,
    lineHeight: 0,
    opacity: 0,
});
const StyledLabel = styled.span(({ jss }) => jss);
const Radio = ({ id, name, value, label, checked = false, disabled = false, ariaLabel, spacing = 'xs', onChange, theme, }) => {
    const [isChecked, setIsChecked] = useState(checked);
    const [isFocus, setIsFocus] = useState(false);
    const refInput = useRef(null);
    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);
    const handleOnInputChange = (e) => {
        setIsChecked(e.target.checked);
        if (typeof onChange === 'function')
            onChange(e);
    };
    const handleOnClickRadio = () => {
        if (refInput && refInput.current) {
            refInput.current.click();
            refInput.current.focus();
        }
    };
    const handleOnFocus = () => {
        setIsFocus(true);
    };
    const handleOnBlur = () => {
        setIsFocus(false);
    };
    const data = {
        'data-focus': isFocus || undefined,
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
    };
    let jssRadio = {
        flex: '0 0 auto',
        width: '22px',
        height: '22px',
        border: `1px solid ${COLORS.GREY_TEXT}`,
        borderRadius: '50%',
        boxSizing: 'border-box',
        background: 'white',
        cursor: 'pointer',
        '&[data-checked="true"]': {
            background: COLORS.DARK,
            boxShadow: 'inset 0 0 0 5px white',
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.radio) {
        jssRadio = deepmerge(jssRadio, theme.radio);
    }
    else {
        jssRadio = deepmerge(jssRadio, {
            color: COLORS.DARK,
            '&[data-focus="true"]': {
                borderColor: COLORS.DARK,
            },
            '&[data-checked="true"]': {
                background: COLORS.PRIMARY,
            },
        });
    }
    let jssLabel = Object.assign({ display: 'inline-block' }, TEXTSTYLE_MEDIUM);
    if (theme === null || theme === void 0 ? void 0 : theme.label) {
        jssLabel = deepmerge(jssLabel, theme.label);
    }
    return (jsxs(StyledRoot, Object.assign({}, data, { children: [jsx(StyledInput$1, { ref: refInput, type: "radio", id: id, name: name, value: value, checked: isChecked, "aria-label": (ariaLabel || label), disabled: disabled, onChange: handleOnInputChange, onFocus: handleOnFocus, onBlur: handleOnBlur }), jsxs(GroupLegacy, Object.assign({ nowrap: true, spacing: spacing }, { children: [jsx(StyledRadio, Object.assign({ jss: jssRadio, onClick: handleOnClickRadio }, data)), label && (jsx(StyledLabel, Object.assign({ jss: jssLabel }, data, { children: label })))] }))] })));
};

const RadioGroup = ({ value, spacing, vertical = false, children, onChange, theme }) => {
    const aChildren = React.Children.toArray(children);
    const [selectedValue, setSelectedValue] = useState(value);
    useEffect(() => {
        setSelectedValue(value);
    }, [value]);
    const handleOnChangeRadio = (e) => {
        setSelectedValue(e.target.value);
        if (typeof onChange === 'function')
            onChange(e.target.value);
    };
    return (jsx(GroupLegacy, Object.assign({ spacing: spacing, vertical: vertical }, { children: aChildren.map((child, i) => {
            const childAsReactElement = child;
            const childProps = childAsReactElement.props || {};
            if (childAsReactElement.type === Radio) {
                return React.cloneElement(childAsReactElement, {
                    key: String(i),
                    checked: childProps.value === selectedValue,
                    theme: deepmerge((theme === null || theme === void 0 ? void 0 : theme.item) || {}, childProps.theme || {}),
                    onChange: (e) => handleOnChangeRadio(e),
                });
            }
            return null;
        }) })));
};

const SelectOption = ({ value, label }) => {
    return (jsxs("div", { children: [value, ": ", label] }));
};

const StyledSelect = styled.div(({ jss }) => jss);
const StyledMenuWrapper = styled.div({
    position: 'relative',
    width: '100%',
    height: 0,
});
const StyledMenu = styled.div(({ jss }) => jss);
const Select = ({ layout, label, value, block = false, iconOpen, iconClose, disabled = false, children, onSelect, theme, }) => {
    const [valueLocal, setValueLocal] = useState(value);
    const [labelLocal, setLabelLocal] = useState(label);
    const [isOpen, setIsOpen] = useState(false);
    const refComponent = useRef(null);
    const aChildren = React.Children.toArray(children);
    const handleOnClickDocument = (e) => {
        if (refComponent && refComponent.current && !refComponent.current.contains(e.target)) {
            setIsOpen(false);
        }
    };
    useEffect(() => {
        document.addEventListener('click', handleOnClickDocument);
        return () => {
            document.removeEventListener('click', handleOnClickDocument);
        };
    }, []);
    useEffect(() => {
        setValueLocal(value);
    }, [value]);
    useEffect(() => {
        setLabelLocal(label);
    }, [label]);
    const handleOnClickButton = () => {
        setIsOpen(!isOpen);
    };
    const handleOnClickOption = (optionValue, optionLabel) => {
        setIsOpen(false);
        setValueLocal(optionValue);
        setLabelLocal(optionLabel);
        if (typeof onSelect === 'function')
            onSelect(optionValue, optionLabel);
    };
    const _iconOpen = iconOpen || ChevronDown;
    const _iconClose = iconClose || ChevronUp;
    let jssSelect = {
        position: 'relative',
        display: block ? 'block' : 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssSelect = deepmerge(jssSelect, theme.root);
    }
    let jssMenu = {
        position: 'absolute',
        zIndex: 10,
        top: 0,
        left: 0,
        minWidth: '100%',
        boxSizing: 'border-box',
        background: 'white',
        boxShadow: COMMON_STYLES.boxShadowDropdown,
    };
    if (theme === null || theme === void 0 ? void 0 : theme.menu) {
        jssMenu = deepmerge(jssMenu, theme.menu);
    }
    else {
        jssMenu = deepmerge(jssMenu, {
            padding: '5px',
            borderRadius: COMMON_STYLES.borderRadius,
        });
    }
    return (jsxs(StyledSelect, Object.assign({ ref: refComponent, jss: jssSelect }, { children: [jsx(Button, { layout: layout, label: labelLocal, iconRight: isOpen ? _iconClose : _iconOpen, onClick: handleOnClickButton, block: block, justify: "space-between", disabled: disabled, theme: {
                    root: theme === null || theme === void 0 ? void 0 : theme.button,
                } }), isOpen && (jsx(StyledMenuWrapper, { children: jsx(StyledMenu, Object.assign({ jss: jssMenu }, { children: aChildren.map((child, i) => {
                        const childAsReactElement = child;
                        if (childAsReactElement.type === SelectOption) {
                            const childProps = childAsReactElement.props || {};
                            const selected = childProps.value === valueLocal;
                            return (jsx(Button, { layout: "option", label: childProps.label, block: true, justify: "flex-start", active: selected, onClick: () => handleOnClickOption(childProps.value, childProps.label), theme: {
                                    root: theme === null || theme === void 0 ? void 0 : theme.option,
                                } }, String(i)));
                        }
                        return null;
                    }) })) }))] })));
};

const StyledSwitch = styled.button(({ jss }) => jss);
const StyledTrack = styled.div(({ jss }) => jss);
const StyledThumb = styled.div(({ jss }) => jss);
const Switch = ({ checked = false, disabled = false, ariaLabel, onChange, theme }) => {
    const [isChecked, setIsChecked] = useState(checked);
    const [isFocus, setIsFocus] = useState(false);
    const [isHover, setIsHover] = useState(false);
    useEffect(() => {
        setIsChecked(checked);
    }, [checked]);
    const handleOnClick = () => {
        setIsChecked(!isChecked);
        if (typeof onChange === 'function')
            onChange(!isChecked);
    };
    const handleOnMouseLeave = (e) => {
        setIsHover(false);
        e.currentTarget.blur();
    };
    const data = {
        'data-checked': isChecked || undefined,
        'data-disabled': disabled || undefined,
        'data-focus': isFocus || undefined,
        'data-hover': isHover || undefined,
    };
    let jssSwitch = {
        appearance: 'none',
        display: 'flex',
        alignItems: 'center',
        margin: 0,
        padding: 0,
        border: 0,
        background: 'transparent',
        position: 'relative',
        cursor: 'pointer',
        '&[data-disabled]': {
            opacity: 0.5,
            cursor: 'default',
            pointerEvents: 'none',
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jssSwitch = deepmerge(jssSwitch, theme.root);
    }
    else {
        jssSwitch = deepmerge(jssSwitch, {
            '&:focus': {
                outline: 0,
            },
        });
    }
    let jssTrack = {
        display: 'inline-block',
        width: '40px',
        height: '20px',
        background: COLORS.grey,
        borderRadius: '10px',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.track) {
        jssTrack = deepmerge(jssTrack, theme.track);
    }
    else {
        jssTrack = deepmerge(jssTrack, {
            '&[data-focus]': {
                background: COLORS.dark,
            },
            '&[data-hover]': {
                background: COLORS.grey,
            },
        });
    }
    let jssThumb = {
        display: 'inline-block',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '20px',
        height: '20px',
        borderRadius: '10px',
        background: COLORS.greyText,
        transition: 'left 0.2s ease',
        '&[data-checked]': {
            left: '20px',
            background: COLORS.primary,
        },
    };
    if (theme === null || theme === void 0 ? void 0 : theme.thumb) {
        jssThumb = deepmerge(jssThumb, theme.thumb);
    }
    return (jsxs(StyledSwitch, Object.assign({ jss: jssSwitch, "aria-label": ariaLabel, disabled: disabled, onClick: handleOnClick, onFocus: () => setIsFocus(true), onBlur: () => setIsFocus(false), onMouseEnter: () => setIsHover(true), onMouseLeave: handleOnMouseLeave }, data, { children: [jsx(StyledTrack, Object.assign({ jss: jssTrack }, data)), jsx(StyledThumb, Object.assign({ jss: jssThumb }, data))] })));
};

const StyledText = styled.div(({ jss }) => jss);
const Text = ({ size, color, fontSize, lineHeight, textAlign, className, children }) => {
    const baseStyle = size === 'small' ? TEXTSTYLE_SMALL : TEXTSTYLE_MEDIUM;
    const jss = Object.assign(Object.assign({}, baseStyle), { color: COLORS[color] || color || COLORS.DARK, textAlign, fontSize: fontSize || baseStyle.fontSize, lineHeight: lineHeight || baseStyle.lineHeight });
    return (jsx(StyledText, Object.assign({ jss: jss, className: className }, { children: children })));
};

const StyledComponent = styled.div(({ jss }) => jss);
const StyledInput = styled.input({
    display: 'none',
});
const UploadButton = (props) => {
    const { accept, multiple, theme, onChange } = props;
    const refInputFile = useRef(null);
    const handleOnClickButton = () => {
        if (refInputFile && refInputFile.current) {
            refInputFile.current.click();
        }
    };
    let jss = {
        display: 'inline-block',
    };
    if (theme === null || theme === void 0 ? void 0 : theme.root) {
        jss = deepmerge(jss, theme.root);
    }
    const buttonProps = Object.assign(Object.assign({}, props), { theme: {
            root: theme === null || theme === void 0 ? void 0 : theme.button,
        } });
    return (jsxs(StyledComponent, Object.assign({ jss: jss }, { children: [jsx(StyledInput, { ref: refInputFile, type: "file", accept: accept, multiple: multiple, onChange: onChange }), jsx(Button, Object.assign({}, buttonProps, { onClick: handleOnClickButton }))] })));
};

export { Block, Button, Checkbox, Collapse, CroppedImage, Dropdown, Grid, GridItem, Group, GroupItem, GroupItemLegacy, GroupLegacy, Image, InlineSVG, Input, Radio, RadioGroup, RatioBox, Select, SelectOption, Switch, Text, UiButton, UiDiv, UiElement, UiFlex, UiImg, UiInput, UiLink, UiSpan, UiText, UploadButton };
